import React, { useState, useMemo, useCallback, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import {
  useServiceRapideStore,
  type AvailabilityBlock,
} from "@store/serviceRapideStore";
import {
  getMonday,
  getWeekDates,
} from "@store/disponibiliteStore";
import { formatTime, snapToGrid } from "@utils/timeGrid";
import { BlockModal } from "./BlockModal";

/* ── Constants ── */

const HOURS_START = 8;
const HOURS_END = 20;
const HOUR_BLOCK_HEIGHT = 80;
const QUARTER_HEIGHT = HOUR_BLOCK_HEIGHT / 4;
const SNAP_QUARTER = 0.25;

const DAY_NAMES_SHORT = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

export const BLOCK_COLORS = {
  recurring: { bg: "#E8E4FD", border: "#D4CCFB", text: "#6C5CE7" },
  punctual: { bg: "#DBEAFE", border: "#BFDBFE", text: "#2563EB" },
};

function getBlockColors(block: AvailabilityBlock) {
  return block.recurring ? BLOCK_COLORS.recurring : BLOCK_COLORS.punctual;
}

function formatHour(h: number): string {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}h${mins > 0 ? String(mins).padStart(2, "0") : ""}`;
}

function formatDayHeader(dateStr: string, index: number): string {
  const d = new Date(dateStr + "T00:00:00");
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${DAY_NAMES_SHORT[index]} ${day}/${month}`;
}

/* ── Drag types ── */

interface BlockDragState {
  blockId: string;
  mode: "move" | "resize";
  startY: number;
  origStartHour: number;
  origDuration: number;
  dayIndex: number;
}

interface CreateDragState {
  dayIndex: number;
  startQuarter: number;
  currentQuarter: number;
}

interface DragPreview {
  blockId?: string;
  dayIndex: number;
  startHour: number;
  durationHours: number;
}

/* ── Component ── */

export function WeekTimeGrid({
  onEditBlock,
}: {
  onEditBlock: (block: AvailabilityBlock) => void;
}) {
  const selectedDate = useServiceRapideStore((s) => s.selectedDate);
  const selectedWorkerId = useServiceRapideStore((s) => s.selectedWorkerId);
  const blocks = useServiceRapideStore((s) => s.blocks);
  const searchQuery = useServiceRapideStore((s) => s.searchQuery);
  const updateBlock = useServiceRapideStore((s) => s.updateBlock);

  const [createModal, setCreateModal] = useState<{
    date: string;
    startHour: number;
    duration: number;
  } | null>(null);

  const blockDragRef = useRef<BlockDragState | null>(null);
  const createDragRef = useRef<CreateDragState | null>(null);
  const didDrag = useRef(false);

  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);

  const monday = useMemo(() => getMonday(selectedDate), [selectedDate]);
  const weekDates = useMemo(() => getWeekDates(monday), [monday]);

  const hours = useMemo(() => {
    const arr: number[] = [];
    for (let h = HOURS_START; h < HOURS_END; h++) arr.push(h);
    return arr;
  }, []);

  const totalGridHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;

  const weekBlocks = useMemo(() => {
    return blocks.filter((b) => {
      if (b.workerId !== selectedWorkerId) return false;
      if (
        searchQuery &&
        !b.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;
      if (weekDates.includes(b.date)) return true;
      if (b.recurring && b.dayOfWeek !== undefined) return true;
      return false;
    });
  }, [blocks, selectedWorkerId, weekDates, searchQuery]);

  const blocksByDay = useMemo(() => {
    const map: Record<number, AvailabilityBlock[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    weekBlocks.forEach((b) => {
      if (b.recurring && b.dayOfWeek !== undefined) {
        map[b.dayOfWeek]?.push(b);
      } else {
        const idx = weekDates.indexOf(b.date);
        if (idx >= 0) map[idx]?.push(b);
      }
    });
    return map;
  }, [weekBlocks, weekDates]);

  /* ── Block drag (move / resize) ── */

  const handleBlockPointerDown = useCallback(
    (
      e: React.PointerEvent,
      block: AvailabilityBlock,
      dayIndex: number,
      mode: "move" | "resize",
    ) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      didDrag.current = false;
      blockDragRef.current = {
        blockId: block.id,
        mode,
        startY: e.clientY,
        origStartHour: block.startHour,
        origDuration: block.durationHours,
        dayIndex,
      };
      setDragPreview({
        blockId: block.id,
        dayIndex,
        startHour: block.startHour,
        durationHours: block.durationHours,
      });
    },
    [],
  );

  const handleBlockPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const bd = blockDragRef.current;
      if (!bd) return;
      const deltaY = e.clientY - bd.startY;
      if (Math.abs(deltaY) > 3) didDrag.current = true;
      const deltaQuarters = Math.round(deltaY / QUARTER_HEIGHT);
      const deltaHours = deltaQuarters * SNAP_QUARTER;

      if (bd.mode === "resize") {
        const newDur = snapToGrid(bd.origDuration + deltaHours, SNAP_QUARTER);
        const clamped = Math.max(
          SNAP_QUARTER,
          Math.min(newDur, HOURS_END - bd.origStartHour),
        );
        setDragPreview({
          blockId: bd.blockId,
          dayIndex: bd.dayIndex,
          startHour: bd.origStartHour,
          durationHours: clamped,
        });
      } else {
        const newStart = snapToGrid(bd.origStartHour + deltaHours, SNAP_QUARTER);
        const clamped = Math.max(
          HOURS_START,
          Math.min(newStart, HOURS_END - bd.origDuration),
        );
        setDragPreview({
          blockId: bd.blockId,
          dayIndex: bd.dayIndex,
          startHour: clamped,
          durationHours: bd.origDuration,
        });
      }
    },
    [],
  );

  const handleBlockPointerUp = useCallback(() => {
    const bd = blockDragRef.current;
    if (!bd) return;
    if (dragPreview && didDrag.current) {
      updateBlock(bd.blockId, {
        startHour: dragPreview.startHour,
        durationHours: dragPreview.durationHours,
      });
    }
    blockDragRef.current = null;
    setDragPreview(null);
  }, [dragPreview, updateBlock]);

  /* ── Create drag (draw new block) ── */

  const handleCellPointerDown = useCallback(
    (e: React.PointerEvent, dayIndex: number) => {
      if (blockDragRef.current) return;
      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const relY = e.clientY - rect.top;
      const quarter = Math.floor(relY / QUARTER_HEIGHT);
      createDragRef.current = {
        dayIndex,
        startQuarter: quarter,
        currentQuarter: quarter,
      };
      const startHour = HOURS_START + quarter * SNAP_QUARTER;
      setDragPreview({
        dayIndex,
        startHour,
        durationHours: SNAP_QUARTER,
      });
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [],
  );

  const handleCellPointerMove = useCallback(
    (e: React.PointerEvent, dayIndex: number) => {
      if (blockDragRef.current) {
        handleBlockPointerMove(e);
        return;
      }
      const cd = createDragRef.current;
      if (!cd || cd.dayIndex !== dayIndex) return;
      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const relY = Math.max(
        0,
        Math.min(e.clientY - rect.top, totalGridHeight),
      );
      const quarter = Math.floor(relY / QUARTER_HEIGHT);
      cd.currentQuarter = quarter;
      const start = Math.min(cd.startQuarter, quarter);
      const end = Math.max(cd.startQuarter, quarter) + 1;
      setDragPreview({
        dayIndex,
        startHour: HOURS_START + start * SNAP_QUARTER,
        durationHours: (end - start) * SNAP_QUARTER,
      });
    },
    [totalGridHeight, handleBlockPointerMove],
  );

  const handleCellPointerUp = useCallback(
    (_e: React.PointerEvent, dayIndex: number) => {
      if (blockDragRef.current) {
        handleBlockPointerUp();
        return;
      }
      const cd = createDragRef.current;
      if (!cd || cd.dayIndex !== dayIndex) return;
      const start = Math.min(cd.startQuarter, cd.currentQuarter);
      const end = Math.max(cd.startQuarter, cd.currentQuarter) + 1;
      const startHour = HOURS_START + start * SNAP_QUARTER;
      const duration = (end - start) * SNAP_QUARTER;

      if (duration >= SNAP_QUARTER) {
        setCreateModal({
          date: weekDates[dayIndex],
          startHour,
          duration,
        });
      }
      createDragRef.current = null;
      setDragPreview(null);
    },
    [weekDates, handleBlockPointerUp],
  );

  return (
    <div
      className="flex flex-col flex-1"
      style={{ minWidth: 0 }}
      onPointerMove={handleBlockPointerMove}
      onPointerUp={handleBlockPointerUp}
    >
      {/* Day headers */}
      <div className="flex flex-row" style={{ paddingLeft: 60 }}>
        {weekDates.slice(0, 5).map((date, i) => (
          <div
            key={date}
            className="flex-1 text-center text-h4 font-medium py-2"
            style={{
              fontFamily: "Poppins",
              color: "#737373",
              borderBottom: "1px solid #E5E5E5",
              borderRight: i < 4 ? "1px solid #E5E5E5" : undefined,
            }}
          >
            {formatDayHeader(date, i)}
          </div>
        ))}
      </div>

      {/* Grid body */}
      <div
        className="flex flex-row flex-1 overflow-y-auto"
        style={{ position: "relative" }}
      >
        {/* Hour labels */}
        <div className="flex flex-col flex-shrink-0" style={{ width: 60 }}>
          {hours.map((h) => (
            <div
              key={h}
              className="flex flex-col"
              style={{ height: HOUR_BLOCK_HEIGHT }}
            >
              {[0, 15, 30, 45].map((m) => (
                <div
                  key={m}
                  className="flex flex-row items-start"
                  style={{ height: QUARTER_HEIGHT }}
                >
                  {m === 0 ? (
                    <>
                      <span
                        className="text-h5 font-medium pr-1 text-right"
                        style={{
                          width: 30,
                          fontFamily: "Poppins",
                          color: "#A1A1A1",
                          marginTop: -6,
                        }}
                      >
                        {h}h
                      </span>
                      <span
                        className="text-h6 font-medium"
                        style={{
                          fontFamily: "Poppins",
                          color: "#D4D4D4",
                          marginTop: -4,
                        }}
                      >
                        00
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{ width: 30 }} />
                      <span
                        className="text-h6 font-medium"
                        style={{
                          fontFamily: "Poppins",
                          color: "#E5E5E5",
                          marginTop: -4,
                        }}
                      >
                        {m}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Day columns (Mon-Fri) */}
        {weekDates.slice(0, 5).map((date, dayIndex) => {
          const dayBlocks = blocksByDay[dayIndex] || [];
          const preview =
            dragPreview && dragPreview.dayIndex === dayIndex
              ? dragPreview
              : null;

          return (
            <div
              key={date}
              className="flex-1 relative"
              style={{
                borderRight:
                  dayIndex < 4 ? "1px solid #E5E5E5" : undefined,
                cursor: "crosshair",
              }}
              onPointerDown={(e) => handleCellPointerDown(e, dayIndex)}
              onPointerMove={(e) => handleCellPointerMove(e, dayIndex)}
              onPointerUp={(e) => handleCellPointerUp(e, dayIndex)}
            >
              {/* Grid lines */}
              {hours.map((h) => (
                <div
                  key={h}
                  style={{
                    height: HOUR_BLOCK_HEIGHT,
                    borderBottom: "1px solid #E5E5E5",
                  }}
                >
                  {[0, 1, 2, 3].map((q) => (
                    <div
                      key={q}
                      style={{
                        height: QUARTER_HEIGHT,
                        borderBottom:
                          q < 3 ? "1px solid #F5F5F5" : undefined,
                      }}
                    />
                  ))}
                </div>
              ))}

              {/* Create-drag preview */}
              {preview && !preview.blockId && (
                <div
                  className="absolute left-1 right-1 pointer-events-none"
                  style={{
                    top:
                      (preview.startHour - HOURS_START) *
                      HOUR_BLOCK_HEIGHT,
                    height: preview.durationHours * HOUR_BLOCK_HEIGHT,
                    background: "rgba(99, 102, 241, 0.15)",
                    border: "2px dashed #6366F1",
                    borderRadius: 6,
                    zIndex: 30,
                  }}
                />
              )}

              {/* Blocks */}
              {dayBlocks.map((block) => {
                const colors = getBlockColors(block);
                const isDragging =
                  dragPreview?.blockId === block.id;
                const displayStart = isDragging
                  ? dragPreview!.startHour
                  : block.startHour;
                const displayDur = isDragging
                  ? dragPreview!.durationHours
                  : block.durationHours;
                const topPx =
                  (displayStart - HOURS_START) * HOUR_BLOCK_HEIGHT;
                const heightPx = displayDur * HOUR_BLOCK_HEIGHT;

                return (
                  <div
                    key={block.id}
                    className="absolute left-1 right-1 flex flex-col overflow-hidden"
                    style={{
                      top: topPx,
                      height: Math.max(heightPx, QUARTER_HEIGHT),
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      boxShadow: isDragging
                        ? "0px 4px 12px rgba(0,0,0,0.2)"
                        : "0px 2px 4px rgba(0,0,0,0.08)",
                      borderRadius: 6,
                      padding: "4px 6px",
                      zIndex: isDragging ? 35 : 20,
                      cursor: "grab",
                      userSelect: "none",
                      opacity: isDragging ? 0.85 : 1,
                      transition: isDragging
                        ? undefined
                        : "opacity 0.15s",
                    }}
                    onPointerDown={(e) =>
                      handleBlockPointerDown(e, block, dayIndex, "move")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!didDrag.current) {
                        onEditBlock(block);
                      }
                    }}
                  >
                    <span
                      className="text-h5 font-medium"
                      style={{
                        color: colors.text,
                        lineHeight: "140%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {block.label}
                    </span>
                    {heightPx >= 50 && (
                      <span
                        className="text-h6"
                        style={{ color: colors.text, opacity: 0.7 }}
                      >
                        {formatHour(displayStart)} –{" "}
                        {formatHour(displayStart + displayDur)}
                      </span>
                    )}
                    {heightPx >= 40 && (
                      <div
                        className="absolute bottom-1 right-1 flex flex-row gap-1"
                        style={{ zIndex: 25 }}
                      >
                        {block.recurring && (
                          <div
                            className="flex items-center justify-center rounded"
                            style={{
                              width: 18,
                              height: 18,
                              background: "rgba(255,255,255,0.6)",
                            }}
                          >
                            <Icon1
                              name="restart"
                              size={12}
                              color={colors.text}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {/* Resize handle */}
                    <div
                      className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                      style={{ height: 8, cursor: "ns-resize" }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        handleBlockPointerDown(
                          e,
                          block,
                          dayIndex,
                          "resize",
                        );
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 2,
                          borderRadius: 1,
                          background: colors.text,
                          opacity: 0.4,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {createModal && (
        <BlockModal
          mode="create"
          workerId={selectedWorkerId}
          initialDate={createModal.date}
          initialStartHour={createModal.startHour}
          initialDuration={createModal.duration}
          onClose={() => setCreateModal(null)}
        />
      )}
    </div>
  );
}
